import { getUniqueId } from "@gettactic/helpers/src/strings/getUniqueId";
import React from "react";
import {
  FormControlWrapperProps,
  FormControlWrapperState,
  getErrorId,
  getHelperTextId
} from "./defines";

export const FormControlWrapperContext = React.createContext<
  FormControlWrapperState | undefined
>(undefined);

export const FormControlWrapperProvider: React.FC<FormControlWrapperProps> = (
  props
) => {
  const { children, ...state } = props;
  const fallbackInputId = React.useId();
  const inputId = React.useMemo(
    () => state.id || fallbackInputId || getUniqueId(),
    [state.id]
  );
  const [counter, setCounter] = React.useState<number>(0);
  return (
    <FormControlWrapperContext.Provider
      value={{
        ...state,
        charactersCounter: counter,
        setCharactersCounter: setCounter,
        id: inputId,
        isInvalid:
          state.isInvalid !== undefined ? state.isInvalid : !!state.error,
        isLoading: props.isLoading
      }}
    >
      {children}
    </FormControlWrapperContext.Provider>
  );
};

export const useFormControlWrapper = () => {
  const context = React.useContext(FormControlWrapperContext);
  return context || ({} as Partial<FormControlWrapperState>);
};

interface FormControlInputA11yProps {
  "aria-invalid"?: boolean;
  "aria-required"?: boolean;
  "aria-describedby"?: string;
}

/**
 * The ARIA attributes a control inside a `FormControlWrapper` needs so screen readers announce
 * its invalid and required state, its helper text and its error. Spread them on the control;
 * values passed by the caller win.
 */
export const useFormControlInputA11y = (
  own: FormControlInputA11yProps = {}
): FormControlInputA11yProps => {
  const { id, isInvalid, isRequired, helperText, error } =
    useFormControlWrapper();
  const describedBy = [
    own["aria-describedby"],
    helperText ? getHelperTextId(id) : undefined,
    error ? getErrorId(id) : undefined
  ]
    .filter(Boolean)
    .join(" ");

  return {
    "aria-invalid": own["aria-invalid"] || isInvalid || undefined,
    "aria-required": own["aria-required"] || isRequired || undefined,
    "aria-describedby": describedBy || undefined
  };
};
